import { APP_CONSTANTS, STORAGE_KEYS } from '../constants'

const API_DEBUG_MODE_QUERY_KEY = 'nebula'
const API_DEBUG_MODE_QUERY_VALUE = 'dust'
const API_DEBUG_QUERY_KEYS = [
  STORAGE_KEYS.restApiURL,
  STORAGE_KEYS.restCustomApiURL,
  STORAGE_KEYS.publicApiURL,
  STORAGE_KEYS.publicCustomApiURL,
]

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

function readStoredValue(key) {
  if (!canUseStorage()) {
    return null
  }
  return window.localStorage.getItem(key)
}

export function normalizeApiBaseUrl(value) {
  if (typeof value !== 'string') {
    return null
  }

  const trimmed = value.trim()
  if (!trimmed) {
    return null
  }

  try {
    const parsed = new URL(trimmed)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null
    }
    return trimmed.replace(/\/+$/, '')
  } catch {
    return null
  }
}

function buildInvalidEntry(key, rawValue) {
  if (rawValue == null || String(rawValue).trim() === '') {
    return null
  }

  if (normalizeApiBaseUrl(rawValue)) {
    return null
  }

  return {
    key,
    value: rawValue,
  }
}

export function getBuiltInApiDomainDefaults() {
  return {
    apiBaseUrl: normalizeApiBaseUrl(APP_CONSTANTS.API_CALLS_DOMAIN),
    publicApiBaseUrl: normalizeApiBaseUrl(APP_CONSTANTS.PUBLIC_API_CALLS_DOMAIN),
  }
}

export function resolveApiDomainConfig() {
  const defaults = getBuiltInApiDomainDefaults()
  const rawStoredDefaultApiBaseUrl = readStoredValue(STORAGE_KEYS.restApiURL)
  const rawCustomApiBaseUrl = readStoredValue(STORAGE_KEYS.restCustomApiURL)
  const rawStoredPublicApiBaseUrl = readStoredValue(STORAGE_KEYS.publicApiURL)
  const rawCustomPublicApiBaseUrl = readStoredValue(STORAGE_KEYS.publicCustomApiURL)

  const storedDefaultApiBaseUrl = normalizeApiBaseUrl(rawStoredDefaultApiBaseUrl)
  const customApiBaseUrl = normalizeApiBaseUrl(rawCustomApiBaseUrl)
  const storedPublicApiBaseUrl = normalizeApiBaseUrl(rawStoredPublicApiBaseUrl)
  const customPublicApiBaseUrl = normalizeApiBaseUrl(rawCustomPublicApiBaseUrl)

  const invalidEntries = [
    buildInvalidEntry(STORAGE_KEYS.restApiURL, rawStoredDefaultApiBaseUrl),
    buildInvalidEntry(STORAGE_KEYS.restCustomApiURL, rawCustomApiBaseUrl),
    buildInvalidEntry(STORAGE_KEYS.publicApiURL, rawStoredPublicApiBaseUrl),
    buildInvalidEntry(STORAGE_KEYS.publicCustomApiURL, rawCustomPublicApiBaseUrl),
  ].filter(Boolean)

  const effectiveApiBaseUrl =
    customApiBaseUrl || storedDefaultApiBaseUrl || defaults.apiBaseUrl
  const effectivePublicApiBaseUrl =
    customPublicApiBaseUrl || storedPublicApiBaseUrl || defaults.publicApiBaseUrl

  return {
    effectiveApiBaseUrl,
    effectivePublicApiBaseUrl,
    apiSource: customApiBaseUrl
      ? 'custom override'
      : storedDefaultApiBaseUrl
        ? 'stored default'
        : 'built-in default',
    publicApiSource: customPublicApiBaseUrl
      ? 'custom override'
      : storedPublicApiBaseUrl
        ? 'stored default'
        : 'built-in default',
    defaultApiBaseUrl: defaults.apiBaseUrl,
    defaultPublicApiBaseUrl: defaults.publicApiBaseUrl,
    storedDefaultApiBaseUrl,
    storedPublicApiBaseUrl,
    customApiBaseUrl,
    customPublicApiBaseUrl,
    invalidEntries,
  }
}

export function getApiBaseUrl() {
  return resolveApiDomainConfig().effectiveApiBaseUrl
}

export function getPublicApiBaseUrl() {
  return resolveApiDomainConfig().effectivePublicApiBaseUrl
}

function writeStoredUrl(key, value) {
  if (!canUseStorage()) {
    return
  }

  const normalizedValue = normalizeApiBaseUrl(value)
  if (!normalizedValue) {
    window.localStorage.removeItem(key)
    return
  }

  window.localStorage.setItem(key, normalizedValue)
}

export function setStoredDefaultApiBaseUrl(value) {
  writeStoredUrl(STORAGE_KEYS.restApiURL, value)
}

export function setStoredPublicApiBaseUrl(value) {
  writeStoredUrl(STORAGE_KEYS.publicApiURL, value)
}

export function setCustomApiBaseUrl(value) {
  writeStoredUrl(STORAGE_KEYS.restCustomApiURL, value)
}

export function setCustomPublicApiBaseUrl(value) {
  writeStoredUrl(STORAGE_KEYS.publicCustomApiURL, value)
}

export function clearCustomApiBaseUrl() {
  if (!canUseStorage()) {
    return
  }
  window.localStorage.removeItem(STORAGE_KEYS.restCustomApiURL)
}

export function clearCustomPublicApiBaseUrl() {
  if (!canUseStorage()) {
    return
  }
  window.localStorage.removeItem(STORAGE_KEYS.publicCustomApiURL)
}

export function getApiDebugQueryConfig(search = typeof window !== 'undefined' ? window.location.search : '') {
  const params = new URLSearchParams(search)
  const requestedEntries = API_DEBUG_QUERY_KEYS.map((key) => ({
    key,
    rawValue: params.get(key),
    normalizedValue: normalizeApiBaseUrl(params.get(key)),
  }))

  const invalidEntries = requestedEntries
    .filter((entry) => entry.rawValue && !entry.normalizedValue)
    .map((entry) => ({
      key: entry.key,
      value: entry.rawValue,
    }))

  const hasRequestedOverrides = requestedEntries.some((entry) => Boolean(entry.normalizedValue))

  return {
    debugPanelRequested:
      params.get(API_DEBUG_MODE_QUERY_KEY) === API_DEBUG_MODE_QUERY_VALUE || hasRequestedOverrides,
    queryParams: params,
    hasRequestedOverrides,
    invalidEntries,
    requestedApiBaseUrl:
      requestedEntries.find((entry) => entry.key === STORAGE_KEYS.restApiURL)?.normalizedValue || null,
    requestedCustomApiBaseUrl:
      requestedEntries.find((entry) => entry.key === STORAGE_KEYS.restCustomApiURL)?.normalizedValue ||
      null,
    requestedPublicApiBaseUrl:
      requestedEntries.find((entry) => entry.key === STORAGE_KEYS.publicApiURL)?.normalizedValue || null,
    requestedCustomPublicApiBaseUrl:
      requestedEntries.find((entry) => entry.key === STORAGE_KEYS.publicCustomApiURL)
        ?.normalizedValue || null,
  }
}

export function applyApiDebugQueryOverrides(search = typeof window !== 'undefined' ? window.location.search : '') {
  const debugQueryConfig = getApiDebugQueryConfig(search)
  if (!debugQueryConfig.hasRequestedOverrides) {
    return false
  }

  if (debugQueryConfig.requestedApiBaseUrl) {
    setStoredDefaultApiBaseUrl(debugQueryConfig.requestedApiBaseUrl)
    if (!debugQueryConfig.requestedCustomApiBaseUrl) {
      clearCustomApiBaseUrl()
    }
  }
  if (debugQueryConfig.requestedCustomApiBaseUrl) {
    setCustomApiBaseUrl(debugQueryConfig.requestedCustomApiBaseUrl)
  }
  if (debugQueryConfig.requestedPublicApiBaseUrl) {
    setStoredPublicApiBaseUrl(debugQueryConfig.requestedPublicApiBaseUrl)
    if (!debugQueryConfig.requestedCustomPublicApiBaseUrl) {
      clearCustomPublicApiBaseUrl()
    }
  }
  if (debugQueryConfig.requestedCustomPublicApiBaseUrl) {
    setCustomPublicApiBaseUrl(debugQueryConfig.requestedCustomPublicApiBaseUrl)
  }

  return true
}

export function removeApiDebugQueryOverridesFromUrl() {
  if (typeof window === 'undefined') {
    return
  }

  const url = new URL(window.location.href)
  API_DEBUG_QUERY_KEYS.forEach((key) => {
    url.searchParams.delete(key)
  })
  window.history.replaceState(null, '', url.toString())
}
